import { billingPreviewInputSchema } from "@ai-crm/shared";
import type {
  AdminBillingViewDTO,
  BillingActionInput,
  BillingConfirmInput,
  BillingConfirmResultDTO,
  BillingPreviewDTO,
  PaidTariffDTO,
  TariffCatalogDTO,
} from "@ai-crm/shared";
import { Link } from "react-router-dom";
import { paymentKeys, usePaymentScope } from "../../lib/payment-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { adminApi, ApiError } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import { formatMoscowDateTime, formatRubTotal } from "../../lib/format";

interface BillingIntent {
  accountId: string;
  preview: BillingPreviewDTO;
  input: BillingConfirmInput;
}

interface BillingOperationPanelProps {
  accountId: string;
  billing: AdminBillingViewDTO;
  catalog: TariffCatalogDTO;
}

type ActionKind = Exclude<BillingActionInput["kind"], "UPGRADE">;
const buttonClass =
  "rounded-md border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50";
const primaryButtonClass = `${buttonClass} border-primary bg-primary text-primary-foreground`;

function allowedKinds(billing: AdminBillingViewDTO): ActionKind[] {
  return (["ASSIGN", "TOPUP"] as const).filter((kind) =>
    kind !== "ASSIGN" && billing.subscription.kind !== "PAID"
      ? false
      : kind === "ASSIGN"
      ? billing.allowedActions.assign
      : billing.allowedActions.topup,
  );
}

function actionLabel(kind: BillingActionInput["kind"]): string {
  switch (kind) {
    case "ASSIGN":
      return "Назначить подписку";
    case "TOPUP":
      return "Докупить диалоги";
    case "UPGRADE":
      return "Повышение по прежним условиям";
  }
}

function actionDescription(kind: ActionKind): string {
  switch (kind) {
    case "ASSIGN":
      return "Служебная операция без счёта. Новый 30-дневный период начинается при подтверждении.";
    case "TOPUP":
      return "Служебная операция без счёта. Диалоги действуют до конца текущего периода и затем сгорают; скидка не применяется.";
  }
}

export function BillingOperationPanel({
  accountId,
  billing,
  catalog,
}: BillingOperationPanelProps): React.JSX.Element {
  const { token } = useAdminAuth();
  const client = useQueryClient();
  const scope = usePaymentScope(accountId);
  const [serviceReason, setServiceReason] = useState("");
  const kinds = allowedKinds(billing);
  const hasPaidTariffs = catalog.definition.paidTariffs.length > 0;
  const selectableKinds = kinds.filter(
    (item) => item === "TOPUP" || hasPaidTariffs,
  );
  const [kind, setKind] = useState<ActionKind>(kinds[0] ?? "TOPUP");
  const [tariffId, setTariffId] = useState(
    catalog.definition.paidTariffs[0]?.id ?? "",
  );
  const [monthlyDialogs, setMonthlyDialogs] = useState(
    String(catalog.definition.paidTariffs[0]?.monthlyDialogs ?? 1),
  );
  const [months, setMonths] = useState(
    String(catalog.definition.paidTariffs[0]?.terms[0]?.months ?? 1),
  );
  const [dialogs, setDialogs] = useState("1");
  const [intent, setIntent] = useState<BillingIntent | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [result, setResult] = useState<BillingConfirmResultDTO | null>(null);
  const [now, setNow] = useState(Date.now());
  const confirmPending = useRef(false);
  const selectedTariff = catalog.definition.paidTariffs.find(
    (tariff) => tariff.id === tariffId,
  );
  const selectedTermAvailable =
    selectedTariff?.terms.some((term) => String(term.months) === months) ??
    false;
  const activeKind =
    (intent?.preview.action.kind === "UPGRADE" ? undefined : intent?.preview.action.kind) ??
    (selectableKinds.includes(kind) ? kind : (selectableKinds[0] ?? kind));
  const locked = pending || uncertain || blocked;
  const expired =
    intent !== null && now >= Date.parse(intent.preview.expiresAt);

  useEffect(() => {
    if (!intent || uncertain) return;
    const delay = Date.parse(intent.preview.expiresAt) - Date.now();
    if (delay <= 0) {
      if (now < Date.parse(intent.preview.expiresAt)) setNow(Date.now());
      return;
    }
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(delay, 60_000),
    );
    return () => window.clearTimeout(timer);
  }, [intent, uncertain, now]);

  function clearPreview() {
    if (uncertain || pending) return;
    setIntent(null);
    setError(null);
  }

  function selectTariff(tariff: PaidTariffDTO) {
    setTariffId(tariff.id);
    setMonthlyDialogs(String(tariff.monthlyDialogs));
    setMonths(String(tariff.terms[0]?.months ?? 1));
    clearPreview();
  }

  function buildAction(): BillingActionInput {
    switch (activeKind) {
      case "ASSIGN":
        return {
          kind: activeKind,
          tariffId,
          monthlyDialogs: Number(monthlyDialogs),
          months: Number(months),
        };
      case "TOPUP":
        return { kind: activeKind, dialogs: Number(dialogs) };
    }
  }

  async function requestPreview() {
    if (!token || pending || uncertain || blocked) return;
    if (activeKind !== "TOPUP" && !hasPaidTariffs) {
      setError(
        "Нет платных тарифов в актуальном каталоге. Назначение недоступно.",
      );
      return;
    }
    if (activeKind !== "TOPUP" && !selectedTariff) {
      setError("Выберите доступный платный тариф из актуального каталога.");
      return;
    }
    if (activeKind === "ASSIGN" && !selectedTermAvailable) {
      setError("Выберите доступный срок из актуального каталога.");
      return;
    }
    if (
      activeKind !== "TOPUP" &&
      selectedTariff &&
      Number(monthlyDialogs) < selectedTariff.monthlyDialogs
    ) {
      setError(
        "Месячный объём должен быть не ниже базового объёма выбранного тарифа.",
      );
      return;
    }
    const input = billingPreviewInputSchema.safeParse({
      action: buildAction(),
      expectedCatalogVersion: catalog.version,
      expectedBillingVersion: billing.billingVersion,
      ...(serviceReason.trim() ? { serviceReason: serviceReason.trim() } : {}),
    });
    if (!input.success) {
      const issue = input.error.issues[0];
      setError(
        `Проверьте параметры операции: ${issue?.message ?? "ошибка ввода"}`,
      );
      return;
    }
    const guard = scope.capture();
    setPending(true);
    setError(null);
    setIntent(null);
    setResult(null);
    try {
      const preview = await adminApi.previewBilling(
        token,
        accountId,
        input.data,
      );
      if (!guard.isCurrent()) return;
      setIntent({
        accountId,
        preview,
        input: {
          requestId: crypto.randomUUID(),
          previewToken: preview.previewToken,
          expectedBillingVersion: preview.billingVersion,
        },
      });
      setNow(Date.now());
    } catch (caught) {
      if (!guard.isCurrent()) return;
      setError(
        caught instanceof ApiError && caught.code === "PAYMENT_REVIEW_REQUIRED"
          ? "Есть неприменённые счета. Проверьте платежи и укажите причину служебной операции."
          : caught instanceof ApiError &&
              caught.code === "BILLING_OPERATION_NOT_ALLOWED"
            ? "Операция больше не разрешена для этой подписки. Обновите данные."
            : "Не удалось получить предпросмотр. Обновите данные и повторите попытку.",
      );
      if (caught instanceof ApiError && caught.status === 409) {
        await Promise.all([
          client.invalidateQueries({
            queryKey: ["admin", "account", accountId, "billing"],
          }),
          client.invalidateQueries({ queryKey: ["admin", "tariffs"] }),
        ]);
      }
    } finally {
      if (guard.isCurrent()) setPending(false);
    }
  }

  async function confirm() {
    if (!token || !intent || pending || confirmPending.current) return;
    if (!uncertain && Date.now() >= Date.parse(intent.preview.expiresAt)) {
      setNow(Date.now());
      setError("Срок действия цены истёк. Получите новый предпросмотр.");
      return;
    }
    if (
      !uncertain &&
      (intent.preview.unappliedInvoiceIds?.length ?? 0) > 0 &&
      !serviceReason.trim()
    ) {
      setError("Укажите причину и получите новый предпросмотр.");
      return;
    }
    const guard = scope.capture();
    confirmPending.current = true;
    setPending(true);
    setError(null);
    try {
      const accepted = await adminApi.confirmBilling(
        token,
        intent.accountId,
        intent.input,
      );
      if (!guard.isCurrent()) return;
      client.setQueryData(
        ["admin", "account", intent.accountId, "billing"],
        accepted.billing,
      );
      setResult(accepted);
      setIntent(null);
      setUncertain(false);
      await Promise.allSettled([
        client.invalidateQueries({
          queryKey: ["admin", "account", intent.accountId],
        }),
        client.invalidateQueries({ queryKey: ["admin", "accounts"] }),
        client.invalidateQueries({
          queryKey: paymentKeys.account(scope.adminId, intent.accountId),
        }),
        client.invalidateQueries({
          queryKey: ["admin", "account", intent.accountId, "audit"],
        }),
      ]);
    } catch (caught) {
      if (!guard.isCurrent()) return;
      if (caught instanceof ApiError && caught.status === 409) {
        setIntent(null);
        setUncertain(false);
        if (caught.code === "BILLING_IDEMPOTENCY_CONFLICT") {
          setBlocked(true);
          setError(
            "Конфликт requestId. Новое назначение заблокировано; проверьте операцию вручную.",
          );
        } else if (caught.code === "BILLING_OPERATION_NOT_ALLOWED") {
          setError(
            "Операция больше не разрешена для этой подписки. Получите актуальные данные.",
          );
        } else {
          setError(
            "Условия или период изменились. Получите новый предпросмотр и подтвердите новую цену.",
          );
        }
        await Promise.allSettled([
          client.invalidateQueries({
            queryKey: ["admin", "account", intent.accountId, "billing"],
          }),
          client.invalidateQueries({ queryKey: ["admin", "tariffs"] }),
        ]);
      } else if (caught instanceof ApiError && caught.status < 500) {
        setIntent(null);
        setUncertain(false);
        setError(
          "Сервер отклонил подтверждение. Получите новый предпросмотр после проверки доступа и параметров.",
        );
      } else {
        setUncertain(true);
        setError(
          "Результат подтверждения неизвестен. Повторите тот же запрос для проверки; параметры операции заблокированы.",
        );
      }
    } finally {
      if (guard.isCurrent()) {
        confirmPending.current = false;
        setPending(false);
      }
    }
  }

  return (
    <section className="panel space-y-4 p-4" aria-label="Ручная операция">
      <div>
        <h2 className="text-lg font-semibold">Ручная операция</h2>
        <p className="text-sm text-muted-foreground">
          Служебная операция без счёта. Не подтверждает оплату и не закрывает
          выставленный счёт.
        </p>
      </div>
      {kinds.length === 0 ? (
        <p>Для текущей подписки операции недоступны.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" aria-label="Вид операции">
            {kinds.map((item) => (
              <button
                key={item}
                className={
                  activeKind === item ? primaryButtonClass : buttonClass
                }
                type="button"
                disabled={locked || (item !== "TOPUP" && !hasPaidTariffs)}
                aria-pressed={activeKind === item}
                onClick={() => {
                  setKind(item);
                  clearPreview();
                }}
              >
                {actionLabel(item)}
              </button>
            ))}
          </div>
          {!hasPaidTariffs && (
            <p className="text-sm text-muted-foreground">
              Нет платных тарифов в актуальном каталоге. Назначение
              недоступно.{billing.allowedActions.topup && billing.subscription.kind === "PAID"
                ? " Докупка по действующей платной подписке остаётся доступной."
                : ""}
            </p>
          )}
          {billing.subscription.kind === "TRIAL" && activeKind === "ASSIGN" && (
            <p className="text-sm text-muted-foreground">
              Назначение платной подписки завершит пробный период сразу.
              Остаток пробных дней и диалогов не переносится, повторная активация невозможна.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {actionDescription(activeKind)}
          </p>
          {activeKind === "TOPUP" ? (
            <label className="block space-y-1 text-sm">
              <span>Количество диалогов для докупки</span>
              <input
                className="control"
                type="number"
                min="1"
                value={dialogs}
                disabled={locked}
                onChange={(event) => {
                  setDialogs(event.target.value);
                  clearPreview();
                }}
              />
            </label>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block space-y-1 text-sm">
                <span>Платный тариф</span>
                <select
                  className="control"
                  value={tariffId}
                  disabled={locked}
                  onChange={(event) => {
                    const tariff = catalog.definition.paidTariffs.find(
                      (item) => item.id === event.target.value,
                    );
                    if (tariff) selectTariff(tariff);
                  }}
                >
                  {!selectedTariff && (
                    <option value={tariffId} disabled>
                      {tariffId
                        ? `${tariffId} — недоступен в текущем каталоге`
                        : "Выберите тариф"}
                    </option>
                  )}
                  {catalog.definition.paidTariffs.map((tariff) => (
                    <option key={tariff.id} value={tariff.id}>
                      {tariff.name} ({tariff.id})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1 text-sm">
                <span>Месячный объём</span>
                <input
                  className="control"
                  type="number"
                  min={selectedTariff?.monthlyDialogs ?? 1}
                  value={monthlyDialogs}
                  disabled={locked}
                  onChange={(event) => {
                    setMonthlyDialogs(event.target.value);
                    clearPreview();
                  }}
                />
              </label>
              {activeKind === "ASSIGN" && (
                <label className="block space-y-1 text-sm">
                  <span>Срок, месяцев</span>
                  <select
                    className="control"
                    value={months}
                    disabled={locked}
                    onChange={(event) => {
                      setMonths(event.target.value);
                      clearPreview();
                    }}
                  >
                    {!selectedTermAvailable && (
                      <option value={months} disabled>
                        {months} мес. — недоступен в текущем каталоге
                      </option>
                    )}
                    {selectedTariff?.terms.map((term) => (
                      <option key={term.months} value={term.months}>
                        {term.months} мес. ({term.discountPercent}% скидка)
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          <label className="block space-y-1 text-sm">
            <span>Причина служебной операции</span>
            <textarea
              className="control"
              value={serviceReason}
              disabled={locked}
              onChange={(event) => {
                setServiceReason(event.target.value);
                clearPreview();
              }}
            />
          </label>
          {!intent && !blocked && (
            <button
              className={primaryButtonClass}
              type="button"
              disabled={pending || (activeKind !== "TOPUP" && !hasPaidTariffs)}
              onClick={() => void requestPreview()}
            >
              Предпросмотр операции
            </button>
          )}
        </>
      )}

      {intent && (
        <div
          className="space-y-3 rounded-md border p-3 text-sm"
          aria-label="Предпросмотр операции"
        >
          {(intent.preview.unappliedInvoiceIds?.length ?? 0) > 0 && (
            <div className="space-y-2 rounded-md border p-3">
              <p>
                Есть неприменённые счета. Действия по счёту выполняются в
                «Платежах»; для служебной операции обязательна причина.
              </p>
              {intent.preview.unappliedInvoiceIds?.map((id) => (
                <Link
                  key={id}
                  className="block break-all text-primary underline"
                  to={`/accounts/${accountId}/payments?invoice=${encodeURIComponent(id)}`}
                >
                  Открыть счёт {id}
                </Link>
              ))}
            </div>
          )}
          <h3 className="font-semibold">
            Предпросмотр: {actionLabel(intent.preview.action.kind)}
          </h3>
          <p>
            Условия операции:{" "}
            {intent.preview.action.kind === "TOPUP"
              ? `${intent.preview.action.dialogs} диалогов`
              : `${intent.preview.action.tariffId}, ${intent.preview.action.monthlyDialogs} диалогов${intent.preview.action.kind === "ASSIGN" ? `, ${intent.preview.action.months} мес.` : ""}`}
          </p>
          <p>
            Рассчитано: {formatMoscowDateTime(intent.preview.calculatedAt)} МСК.
            Цена действует 5 минут, до{" "}
            {formatMoscowDateTime(intent.preview.expiresAt)} МСК.
          </p>
          <p>
            Базовая стоимость месяца: {intent.preview.breakdown.baseMonthlyRub}{" "}
            ₽
          </p>
          {intent.preview.breakdown.undiscountedRub !== null && (
            <p>Без скидки: {intent.preview.breakdown.undiscountedRub} ₽</p>
          )}
          {intent.preview.breakdown.discountRub !== null && (
            <p>Скидка в рублях: {intent.preview.breakdown.discountRub} ₽</p>
          )}
          {intent.preview.action.kind === "UPGRADE" && (
            <>
              <p>
                Старая база за месяц:{" "}
                {intent.preview.breakdown.oldBaseMonthlyRub} ₽; новая база:{" "}
                {intent.preview.breakdown.baseMonthlyRub} ₽.
              </p>
              <p>
                Оставшееся время: {intent.preview.breakdown.remainingMs} мс /
                период {intent.preview.breakdown.periodMs} мс. Окончательная
                доплата рассчитана сервером.
              </p>
            </>
          )}
          <p>
            Итого к назначению:{" "}
            <strong>{formatRubTotal(intent.preview.breakdown.totalRub)}</strong>
          </p>
          {expired && !uncertain && (
            <p className="text-danger">
              Срок действия цены истёк. Получите новый предпросмотр.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {!uncertain && (
              <button
                className={buttonClass}
                type="button"
                disabled={pending}
                onClick={clearPreview}
              >
                Назад
              </button>
            )}
            <button
              className={primaryButtonClass}
              type="button"
              disabled={pending || (expired && !uncertain)}
              onClick={() => void confirm()}
            >
              {uncertain ? "Повторить подтверждение" : "Подтвердить назначение"}
            </button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {result && (
        <div
          role="status"
          className="rounded-md border border-primary/30 p-3 text-sm"
        >
          <p>
            Операция {result.operation.id} выполнена как служебная операция без
            счёта.
          </p>
          <p>
            Время назначения:{" "}
            {formatMoscowDateTime(result.operation.effectiveAt)} МСК.
          </p>
          <p>Сумма операции: {formatRubTotal(result.operation.totalRub)}.</p>
        </div>
      )}
    </section>
  );
}
